import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE, CITIES } from "@/lib/site-data";
import { LIMO_LANDINGS } from "@/lib/commercial-landing";
import { ldJson, faqJsonLd, hoursSpec24x7, imageObjectJsonLd } from "@/lib/seo-schema";
import { breadcrumbJsonLd } from "@/lib/breadcrumbs";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AnswerBlock } from "@/components/AnswerBlock";
import { ContactButtons } from "@/components/ContactButtons";
import { ServiceStandards } from "@/components/ServiceStandards";
import { CTABand } from "@/components/CTA";
import { HOURLY_RATES } from "@/lib/pricing";
import sprinterImg from "@/assets/fleet-sprinter.jpg";

const SITE_URL = "https://premierexecutivechauffeur.com";
const URL = `${SITE_URL}/sprinter-van-transportation`;
const OG_IMAGE = `${SITE_URL}/og-default.jpg`;
const DESCRIPTION =
  "Mercedes-Benz Sprinter vans for 6–14 passengers across Silicon Valley: corporate offsites, airport groups, weddings and wine tours. From $175/hr.";

const SPRINTER_RATES = HOURLY_RATES.filter((r) => r.vehicleClass === "Sprinter");
const SPRINTER_MIN = Math.min(...SPRINTER_RATES.map((r) => r.rate));

const ANSWER = {
  question: "What is Sprinter van transportation and who is it for?",
  answer:
    `Sprinter van transportation is chauffeured group travel in a Mercedes-Benz Sprinter carrying six to fourteen passengers with luggage. Premier Executive Chauffeur runs Sprinters across Silicon Valley and the Bay Area for corporate offsites, airport groups, weddings, and wine tours. Hourly service starts at $${SPRINTER_MIN} per hour with a five-hour minimum. Call ${SITE.phone}.`,
  facts: [
    { label: "Capacity", value: "6–14 passengers depending on configuration, plus luggage" },
    { label: "Pricing", value: `From $${SPRINTER_MIN}/hr, five-hour minimum · flat rates for transfers` },
    { label: "Coverage", value: "Silicon Valley, San Francisco, East Bay, Santa Cruz, Monterey, Napa" },
    { label: "Airports", value: "SFO, SJC, OAK and MRY with flight tracking and FBO access" },
  ],
};

const USE_CASES = [
  {
    title: "Corporate offsites and campus tours",
    text: "Move an entire visiting team between Sand Hill Road, the campus, lunch, and the hotel in one vehicle instead of a four-car caravan. Monthly invoicing and cost-center reporting are available for corporate accounts.",
  },
  {
    title: "Airport groups and crew transfers",
    text: "One Sprinter handles a family of eight with ski bags or a delegation landing together. At SJC, the party meets at the Charter Service pickup booth for Terminal A or B; SFO groups receive their assigned bus-courtyard instructions before arrival.",
  },
  {
    title: "Weddings and wedding-party shuttles",
    text: "Bridal party to the ceremony, guests between hotel and venue, and a clean getaway at the end of the night — all on a written timeline with a backup vehicle staged nearby.",
  },
  {
    title: "Napa and Sonoma wine tours",
    text: "A full-day hourly charter with a chauffeur who knows the appointment cadence at Opus One, Cakebread, and Domaine Carneros. Coolers and cases ride in the rear luggage area, not on your lap.",
  },
  {
    title: "Sporting events and concerts",
    text: "Group drop-off and post-event pickup at Levi's Stadium, SAP Center, Chase Center, and Shoreline Amphitheatre, with a dispatcher watching the clock so the van is staged before the encore ends.",
  },
  {
    title: "Private aviation and FBO groups",
    text: "Ramp-side pickups at Signature, Atlantic, Jet Aviation, and Moffett so a full jet load transfers to one vehicle without a second stop.",
  },
];

const SPRINTER_FAQS = [
  {
    q: "How many passengers fit in a Mercedes-Benz Sprinter?",
    a: "Our Sprinters run in two configurations: an executive layout seating up to six with generous legroom and luggage space, and a passenger layout seating up to fourteen. We confirm the exact configuration when you book so seating and luggage both work.",
  },
  {
    q: "How much does Sprinter van transportation cost?",
    a: `Hourly Sprinter charters start at $${SPRINTER_MIN} per hour with a five-hour minimum; the fourteen-passenger configuration is quoted higher. Airport and point-to-point transfers are flat rates confirmed in writing, with tolls, parking, airport fees, and any pre-included gratuity shown before you book.`,
  },
  {
    q: "Is there luggage space in the Sprinter?",
    a: "Yes. The rear luggage area handles roughly one large checked bag per passenger in the executive layout, and we plan capacity in advance for ski, golf, or trade-show equipment. Oversized loads can be split across a Sprinter and a support SUV.",
  },
  {
    q: "Can the Sprinter do multi-day or multi-stop itineraries?",
    a: "Yes. Multi-day corporate programs and multi-stop itineraries are run as hourly as-directed charters with the same chauffeur where possible, so briefing happens once. We build a written timeline before day one.",
  },
  {
    q: "Do you serve airports and FBOs with the Sprinter?",
    a: "Yes. At SJC, groups use the Charter Service pickup booth for the terminal where their flight arrives. At SFO, international groups use Bus Courtyard A or G and domestic groups use the designated terminal courtyard. We also dispatch to OAK, MRY, Signature, Atlantic, Jet Aviation, and Moffett Federal Airfield.",
  },
  {
    q: "What if we need more than fourteen seats?",
    a: "Larger groups are staged as a convoy — multiple Sprinters, or Sprinters plus Suburban LT SUVs — managed by one dispatcher on a single timeline so everyone arrives together rather than in waves.",
  },
];

export const Route = createFileRoute("/sprinter-van-transportation")({
  head: () => ({
    meta: [
      { title: "Sprinter Van Transportation | Bay Area Group Travel" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Mercedes-Benz Sprinter Van Transportation — 6–14 Passengers" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Sprinter Van Transportation — Premier Executive Chauffeur" },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Sprinter Van Transportation", path: "/sprinter-van-transportation" },
      ]),
      {
        type: "application/ld+json" as const,
        children: ldJson({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": URL,
              url: URL,
              name: "Sprinter Van Transportation",
              description: DESCRIPTION,
              abstract: ANSWER.answer,
              inLanguage: "en-US",
              isPartOf: { "@id": `${SITE_URL}#website` },
              about: { "@id": `${SITE_URL}#business` },
              breadcrumb: { "@id": `${URL}#breadcrumb` },
              primaryImageOfPage: imageObjectJsonLd({
                "@id": `${URL}#image`,
                url: `${SITE_URL}/og-default.jpg`,
                contentUrl: `${SITE_URL}/og-default.jpg`,
                caption:
                  "Mercedes-Benz Sprinter executive van used for chauffeured group transportation across Silicon Valley and the San Francisco Bay Area",
              }),
              speakable: { "@type": "SpeakableSpecification", cssSelector: [".answer-first"] },
            },
            {
              "@type": "Service",
              "@id": `${URL}#service`,
              name: "Sprinter van transportation",
              serviceType: "Group van and shuttle chauffeur service",
              description: DESCRIPTION,
              provider: { "@id": `${SITE_URL}#business` },
              areaServed: [
                { "@type": "AdministrativeArea", name: "Silicon Valley" },
                { "@type": "AdministrativeArea", name: "San Francisco Bay Area" },
                { "@type": "AdministrativeArea", name: "Monterey Peninsula" },
                { "@type": "AdministrativeArea", name: "Napa and Sonoma" },
              ],
              hoursAvailable: hoursSpec24x7,
              availableChannel: {
                "@type": "ServiceChannel",
                serviceUrl: URL,
                servicePhone: SITE.phone,
                availableLanguage: "English",
              },
              offers: SPRINTER_RATES.map((r) => ({
                "@type": "Offer",
                name: `${r.vehicle} — ${r.capacity}`,
                priceCurrency: "USD",
                price: r.rate,
                availability: "https://schema.org/InStock",
                url: SITE.bookUrl,
                priceSpecification: {
                  "@type": "UnitPriceSpecification",
                  priceCurrency: "USD",
                  price: r.rate,
                  unitCode: "HUR",
                  referenceQuantity: { "@type": "QuantitativeValue", value: r.minimumHours, unitCode: "HUR" },
                },
              })),
            },
            {
              ...faqJsonLd(SPRINTER_FAQS),
              "@id": `${URL}#faq`,
              url: URL,
              isPartOf: { "@id": `${SITE_URL}#website` },
              about: { "@id": `${SITE_URL}#business` },
            },
          ],
        }),
      },
    ],
  }),
  component: SprinterPage,
});

function SprinterPage() {
  const topCities = CITIES.slice(0, 6);
  return (
    <>
      <section className="relative overflow-hidden border-b border-border">
        <img
          src={sprinterImg}
          alt="Black Mercedes-Benz Sprinter executive van staged for chauffeured group transportation in Silicon Valley"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background" />
        <div className="relative container-x py-24 md:py-32 max-w-3xl">
          <Breadcrumbs items={[{ name: "Home", to: "/" }, { name: "Sprinter Van Transportation" }]} className="mb-4" />
          <p className="eyebrow mb-4">Group travel</p>
          <h1 className="text-4xl md:text-6xl mb-4 text-balance">
            Sprinter Van Transportation for 6–14 Passengers
          </h1>
          <p className="text-muted-foreground text-lg mb-6">
            Chauffeured Mercedes-Benz Sprinter service across Silicon Valley, San Francisco, the East Bay, and wine country — from ${SPRINTER_MIN}/hr with a five-hour minimum.
          </p>
          <ContactButtons context="Sprinter van transportation" />
        </div>
      </section>

      <div className="container-x py-16 space-y-16 max-w-3xl">
        <AnswerBlock content={ANSWER} />

        <section className="space-y-4 text-muted-foreground">
          <p>
            A Sprinter solves the problem that sedans and SUVs cannot: keeping a group together. When eight people need to arrive at the same door at the same minute, splitting them across three cars introduces three chances to be late. Premier Executive Chauffeur operates Mercedes-Benz Sprinter vans as pre-arranged charters, dispatched by a live team and driven by CPUC-licensed chauffeurs who run these routes daily.
          </p>
          <p>
            The cabin is built for working travel, not for hauling. Executive seating with real legroom, USB power at the seats, climate control the passengers can feel in the back row, and a rear luggage area that swallows checked bags, trade-show cases, ski bags, and wine. Step-in height is low enough for older guests and anyone in formal wear, which matters more at a wedding than any spec sheet.
          </p>
          <p>
            Pricing is straightforward. Hourly as-directed charters begin at ${SPRINTER_MIN} per hour with a five-hour minimum, and the fourteen-passenger configuration is quoted separately. Airport and point-to-point transfers are flat rates confirmed in writing at booking. The confirmation itemizes tolls, parking, airport fees, and any pre-included gratuity; gratuity is otherwise excluded.
          </p>
          <p>
            Operationally, every Sprinter booking gets the same treatment as an executive sedan run: a named chauffeur with vehicle details sent before pickup, flight tracking on airport work, a written timeline for multi-stop days, and staffed dispatch reachable by phone, text, or WhatsApp. Groups larger than fourteen are staged as convoys under a single dispatcher rather than handed to separate vendors.
          </p>
        </section>

        <section>
          <h2 className="text-2xl md:text-3xl mb-6">What clients book Sprinters for</h2>
          <div className="grid gap-6 sm:grid-cols-2">
            {USE_CASES.map((u) => (
              <div key={u.title} className="border border-border bg-surface p-5">
                <p className="font-display text-lg text-foreground mb-2">{u.title}</p>
                <p className="text-sm text-muted-foreground">{u.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl md:text-3xl mb-4">Sprinter hourly rates</h2>
          <div className="overflow-x-auto border border-border">
            <table className="w-full text-sm">
              <caption className="sr-only">Published hourly rates for Mercedes-Benz Sprinter van transportation</caption>
              <thead className="bg-surface-2 text-left">
                <tr>
                  <th scope="col" className="p-3">Vehicle</th>
                  <th scope="col" className="p-3">Capacity</th>
                  <th scope="col" className="p-3">Hourly</th>
                  <th scope="col" className="p-3">Minimum</th>
                </tr>
              </thead>
              <tbody>
                {SPRINTER_RATES.map((r) => (
                  <tr key={r.vehicle + r.rate} className="border-t border-border">
                    <td className="p-3 text-foreground">{r.vehicle}</td>
                    <td className="p-3 text-muted-foreground">{r.capacity}</td>
                    <td className="p-3 text-gold">${r.rate}/hr</td>
                    <td className="p-3 text-muted-foreground">{r.minimumHours} hours</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-muted-foreground mt-3">
            Point-to-point and airport transfers are quoted as flat rates instead of hourly. Ask dispatch which structure is cheaper for your itinerary — we will tell you honestly.
          </p>
        </section>

        <ServiceStandards
          heading="How we run every Sprinter job"
          intro="The same operating standards apply whether the vehicle carries one executive or fourteen wedding guests."
        />

        <section>
          <h2 className="text-2xl md:text-3xl mb-4">Sprinter van FAQs</h2>
          <dl className="space-y-6">
            {SPRINTER_FAQS.map((f) => (
              <div key={f.q}>
                <dt className="font-display text-lg text-foreground mb-1">{f.q}</dt>
                <dd className="text-muted-foreground">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section>
          <h2 className="text-2xl md:text-3xl mb-4">Sprinter service by city</h2>
          <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {topCities.map((c) => (
              <li key={c.slug}>
                <Link to="/service-areas/$city" params={{ city: c.slug }} className="text-gold hover:underline">
                  {c.name}
                  <span className="sr-only"> group van service (Sprinter page)</span>
                </Link>
              </li>
            ))}
          </ul>
          <ul className="grid gap-3 sm:grid-cols-2 text-sm mt-8">
            <li>
              <Link to="/services/$service" params={{ service: "sprinter-groups" }} className="text-gold hover:underline">
                Sprinter group service overview
                <span className="sr-only"> — service catalog entry</span>
              </Link>
            </li>
            <li>
              <Link to="/fleet" className="text-gold hover:underline">
                See the Sprinter in our fleet
                <span className="sr-only"> — vehicle specifications</span>
              </Link>
            </li>
            <li>
              <Link to="/limo-service" className="text-gold hover:underline">
                Bay Area limo service hub
                <span className="sr-only"> — from the Sprinter page</span>
              </Link>
            </li>
            <li>
              <Link to="/limo-service/$city" params={{ city: LIMO_LANDINGS[0].slug }} className="text-gold hover:underline">
                {LIMO_LANDINGS[0].keyword}
                <span className="sr-only"> — from the Sprinter page</span>
              </Link>
            </li>
            <li>
              <Link to="/services/$service" params={{ service: "wine-tours" }} className="text-gold hover:underline">
                Napa &amp; Sonoma wine tours
                <span className="sr-only"> — popular Sprinter itinerary</span>
              </Link>
            </li>
            <li>
              <Link to="/price-guide" className="text-gold hover:underline">
                Download the price guide
                <span className="sr-only"> — from the Sprinter page</span>
              </Link>
            </li>
          </ul>
        </section>
      </div>

      <CTABand
        heading="Reserve a Sprinter for your group"
        sub={`Tell us the headcount, luggage, and timeline — dispatch confirms in writing. Call ${SITE.phone}.`}
      />
    </>
  );
}
